import React, { useCallback, useEffect, useState } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';

const BASE = "https://ltwph7gpvb.execute-api.us-east-1.amazonaws.com";
const PAYMENT = ["Letter of credit", "Telegraphic transfer", "Documents against payment"];
const TRADE_STATUS = ["Requested", "In progress", "Completed", "Cancelled"];
const TASK_STATUS = ["Pending", "In progress", "Done"];
const DOC_STATUS = ["Missing", "Submitted", "Approved"];

async function api(path, options = {}) {
  const session = await fetchAuthSession();
  const token = session.tokens?.idToken?.toString();
  const res = await fetch(`${BASE}${path}`, {
    ...options,
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" }
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.message || `Error ${res.status}`);
  return data;
}

const badgeFor = (s) =>
  ({ "Requested": "medium", "In progress": "transit", "Completed": "low", "Cancelled": "other" }[s] ?? "other");

const esc = (s) =>
  String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));

const hasDraft = (name) => /sales|bill of lading|manifest/i.test(name);

function draftHtml(docName, t) {
  const n = docName.toLowerCase();
  const row = (k, v) => `<tr><td class="k">${esc(k)}</td><td>${esc(v)}</td></tr>`;
  const qty = `${Number(t.quantity).toLocaleString()} MT`;
  let title;
  let body;

  if (n.includes("sales")) {
    title = "Sales and Purchase Agreement (draft)";
    body = row("Seller", t.seller) + row("Buyer", t.buyer) + row("Commodity", t.cargo) +
      row("Quantity", qty) + row("Load port", t.loadPort) + row("Discharge port", t.dischargePort) +
      row("Laycan", `${t.laycanStart} to ${t.laycanEnd}`) + row("Payment terms", t.paymentTerms) +
      row("Price", "To be agreed") + row("Quality and quantity", "As per inspection at load port") +
      row("Governing law", "To be agreed");
  } else if (n.includes("bill of lading")) {
    title = "Bill of Lading (draft)";
    body = row("Shipper", t.seller) + row("Consignee", t.buyer) + row("Vessel", "To be nominated") +
      row("Port of loading", t.loadPort) + row("Port of discharge", t.dischargePort) +
      row("Description of goods", t.cargo) + row("Quantity", qty) +
      row("Freight", "As per charter party") + row("Shipped on board", "Date to be completed");
  } else if (n.includes("manifest")) {
    title = "Cargo Manifest (draft)";
    body = row("Trade reference", t.tradeId) + row("Cargo", t.cargo) + row("Total quantity", qty) +
      row("Load port", t.loadPort) + row("Discharge port", t.dischargePort) +
      row("Shipper", t.seller) + row("Receiver", t.buyer) + row("Vessel", "To be nominated");
  } else {
    return null;
  }

  return `<!doctype html><html><head><meta charset="utf-8"><title>${esc(title)}</title>
<style>body{font-family:Segoe UI,Arial,sans-serif;margin:40px;color:#1a2b3c}h1{font-size:22px}
table{border-collapse:collapse;width:100%}td{border:1px solid #c9d3df;padding:10px;font-size:14px}
td.k{width:32%;background:#f3f6fa;font-weight:600}
.warn{background:#fff2dc;padding:10px;border-radius:6px;font-size:13px;margin-bottom:20px}
.sig{margin-top:60px;display:flex;gap:60px}.sig div{flex:1;border-top:1px solid #1a2b3c;padding-top:6px;font-size:13px}</style></head>
<body><div class="warn">DRAFT generated from trade ${esc(t.tradeId)}. Not a legal document. Have it reviewed by a qualified shipping or trade lawyer before use. Press Ctrl+P to print or save as PDF.</div>
<h1>${esc(title)}</h1><table>${body}</table>
<div class="sig"><div>Signed for the Seller</div><div>Signed for the Buyer</div></div></body></html>`;
}

function openDraft(docName, trade) {
  const html = draftHtml(docName, trade);
  const w = window.open("", "_blank");
  if (!w) {
    alert("Please allow pop-ups for this site to open the draft.");
    return;
  }
  w.document.write(html);
  w.document.close();
  w.focus();
}

const StatusSelect = ({ value, options, disabled, onChange }) => (
  <select className="sel" value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)}>
    {options.map((o) => <option key={o}>{o}</option>)}
  </select>
);

function NewTrade({ ports, cargos, onCancel, onCreated }) {
  const [form, setForm] = useState({
    buyer: "", seller: "", cargo: "LPG", quantity: 40000,
    loadPort: "Mundra", dischargePort: "Fujairah",
    laycanStart: "", laycanEnd: "", paymentTerms: PAYMENT[0], notes: ""
  });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  async function submit() {
    setError("");
    setBusy(true);
    try {
      const t = await api("/trades", { method: "POST", body: JSON.stringify(form) });
      onCreated(t.tradeId);
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  }

  const field = (label, key, type = "text") => (
    <div><label>{label}</label><input type={type} value={form[key]} onChange={set(key)} /></div>
  );
  const select = (label, key, options) => (
    <div>
      <label>{label}</label>
      <select value={form[key]} onChange={set(key)}>{options.map((o) => <option key={o}>{o}</option>)}</select>
    </div>
  );

  return (
    <div className="card">
      <h2>New trade request</h2>
      <div className="form">
        {field("Seller", "seller")}
        {field("Buyer", "buyer")}
        {select("Cargo", "cargo", cargos)}
        {field("Quantity (MT)", "quantity", "number")}
        {select("Load port", "loadPort", ports)}
        {select("Discharge port", "dischargePort", ports)}
        {field("Laycan start", "laycanStart", "date")}
        {field("Laycan end", "laycanEnd", "date")}
        {select("Payment terms", "paymentTerms", PAYMENT)}
      </div>
      <div className="form">
        <div style={{ gridColumn: "1 / -1" }}>
          <label>Notes (optional)</label>
          <textarea rows="3" value={form.notes} onChange={set("notes")} />
        </div>
      </div>
      <button className="btn" onClick={submit} disabled={busy}>{busy ? "Submitting..." : "Submit trade"}</button>{" "}
      <button className="back" onClick={onCancel}>Cancel</button>
      {error && <div className="err">{error}</div>}
    </div>
  );
}

function TradeDetail({ id, canManage, onBack }) {
  const [t, setT] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        setT(await api(`/trades/${id}`));
      } catch (err) {
        setError(err.message);
      }
    })();
  }, [id]);

  async function send(body) {
    setError("");
    try {
      setT(await api(`/trades/${id}/update`, { method: "POST", body: JSON.stringify(body) }));
    } catch (err) {
      setError(err.message);
    }
  }

  return (
    <div>
      <button className="back" onClick={onBack}>← Back to trades</button>
      {error && <div className="err">{error}</div>}
      {!t && !error && <p className="note">Loading trade...</p>}
      {t && (
        <>
          <div className="card">
            <h2>{t.tradeId} <span className={`badge ${badgeFor(t.status)}`}>{t.status}</span></h2>
            <div className="kvgrid">
              <div className="kv"><span>Seller</span><b>{t.seller}</b></div>
              <div className="kv"><span>Buyer</span><b>{t.buyer}</b></div>
              <div className="kv"><span>Cargo</span><b>{t.cargo}</b></div>
              <div className="kv"><span>Quantity</span><b>{Number(t.quantity).toLocaleString()} MT</b></div>
              <div className="kv"><span>Route</span><b>{t.loadPort} → {t.dischargePort}</b></div>
              <div className="kv"><span>Laycan</span><b>{t.laycanStart} to {t.laycanEnd}</b></div>
              <div className="kv"><span>Payment terms</span><b>{t.paymentTerms}</b></div>
              <div className="kv"><span>Requested by</span><b>{t.createdBy}</b></div>
            </div>
            {t.notes && <p className="note">Notes: {t.notes}</p>}
            <p style={{ marginTop: 14 }}>
              <label style={{ display: "inline", marginRight: 8 }}>Trade status</label>
              <StatusSelect value={t.status} options={TRADE_STATUS} disabled={!canManage}
                onChange={(v) => send({ kind: "status", status: v })} />
              {!canManage && <span className="note"> Only operations staff can change this.</span>}
            </p>
          </div>

          <div className="two">
            <div className="card">
              <h2>Tasks</h2>
              <table>
                <tbody>
                  {t.tasks.map((task) => (
                    <tr key={task.id}>
                      <td>{task.title}<br /><span className="note">{task.owner}</span></td>
                      <td><StatusSelect value={task.status} options={TASK_STATUS} disabled={!canManage}
                        onChange={(v) => send({ kind: "task", id: task.id, status: v })} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="card">
              <h2>Documents</h2>
              <table>
                <tbody>
                  {t.documents.map((d) => (
                    <tr key={d.id}>
                      <td>{d.name}</td>
                      <td>{hasDraft(d.name) && <button className="link" onClick={() => openDraft(d.name, t)}>Draft</button>}</td>
                      <td><StatusSelect value={d.status} options={DOC_STATUS} disabled={!canManage}
                        onChange={(v) => send({ kind: "document", id: d.id, status: v })} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="note">Draft documents are filled in from the trade data. Review them with a lawyer before use.</p>
            </div>
          </div>

          <div className="card">
            <h2>History</h2>
            <div className="tl">
              {[...(t.history ?? [])].reverse().map((h, i) => (
                <div key={i}><b>{h.text}</b> <span className="note">{new Date(h.at).toLocaleString()} · {h.by}</span></div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default function Trades() {
  const [view, setView] = useState("list");
  const [trades, setTrades] = useState([]);
  const [ports, setPorts] = useState([]);
  const [cargos, setCargos] = useState([]);
  const [canManage, setCanManage] = useState(false);
  const [error, setError] = useState("");

  const loadList = useCallback(async () => {
    try {
      setTrades(await api("/trades"));
    } catch (err) {
      setError(err.message);
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const session = await fetchAuthSession();
        const groups = session.tokens?.idToken?.payload?.["cognito:groups"] ?? [];
        setCanManage(groups.includes("admin") || groups.includes("ops"));
        const p = await api("/ports");
        setPorts(p.ports);
        setCargos(p.cargos);
      } catch {
        setError("Could not load setup data");
      }
      loadList();
    })();
  }, [loadList]);

  if (view === "new") {
    return <NewTrade ports={ports} cargos={cargos} onCancel={() => setView("list")}
      onCreated={(id) => { loadList(); setView(id); }} />;
  }
  if (view !== "list") {
    return <TradeDetail id={view} canManage={canManage} onBack={() => { loadList(); setView("list"); }} />;
  }

  return (
    <div className="card">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h2>Trades</h2>
        <button className="btn" onClick={() => setView("new")}>+ New trade</button>
      </div>
      {error && <div className="err">{error}</div>}
      {trades.length === 0 && !error && <p className="note">No trades yet. Click "New trade" to submit one.</p>}
      {trades.length > 0 && (
        <div className="tablewrap">
          <table>
            <thead>
              <tr><th>Reference</th><th>Cargo</th><th>Route</th><th>Quantity</th><th>Laycan</th><th>Status</th><th>Tasks</th><th>Documents</th></tr>
            </thead>
            <tbody>
              {trades.map((t) => (
                <tr key={t.tradeId}>
                  <td><button className="link" onClick={() => setView(t.tradeId)}>{t.tradeId}</button></td>
                  <td>{t.cargo}</td>
                  <td>{t.loadPort} → {t.dischargePort}</td>
                  <td>{Number(t.quantity).toLocaleString()} MT</td>
                  <td>{t.laycanStart}</td>
                  <td><span className={`badge ${badgeFor(t.status)}`}>{t.status}</span></td>
                  <td>{t.taskDone}/{t.taskTotal}<div className="bar"><i style={{ width: `${(t.taskDone / t.taskTotal) * 100}%` }} /></div></td>
                  <td>{t.docDone}/{t.docTotal}<div className="bar"><i style={{ width: `${(t.docDone / t.docTotal) * 100}%` }} /></div></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}